import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import { useData } from '../context/DataContext';
import TutorSessionsReport from './TutorSessionsReport'; // New Import
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDollarSign, FiCalendar, FiUsers, FiTrendingUp, FiCheckCircle, FiClock, FiPieChart, FiBarChart2, FiActivity, FiList } = FiIcons;

const ReportManagement = () => {
  const { students, sessions, formatCurrency, appSettings, calculateDashboardStats } = useData();
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'tutor-sessions'
  
  // Guard against missing function in context
  const stats = calculateDashboardStats ? calculateDashboardStats() : {
    totalRevenue: 0,
    totalSessions: 0,
    totalPaidSessions: 0,
    activeStudents: 0
  };

  // Process Data for Charts
  const chartData = useMemo(() => {
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      last6Months.push(d.toLocaleString('default', { month: 'long' }));
    }

    const revenueByMonth = last6Months.map(month => {
      let monthRevenue = 0;
      Object.values(sessions).forEach(studentSessions => {
        (studentSessions[month] || []).forEach(s => {
          if (s.isPaid) monthRevenue += parseFloat(s.feePerSession || 0);
        });
      });
      return monthRevenue;
    });

    return { months: last6Months, revenue: revenueByMonth };
  }, [sessions]);

  const revenueChartOption = {
    tooltip: { trigger: 'axis', backgroundColor: '#111827', textStyle: { color: '#fff' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: chartData.months,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#9CA3AF', fontWeight: 'bold' }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { type: 'dashed', color: '#F3F4F6' } },
      axisLabel: { color: '#9CA3AF' }
    },
    series: [{
      name: 'Revenue',
      data: chartData.revenue,
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: { color: appSettings.brandColor || '#4f46e5' },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: `${appSettings.brandColor || '#4f46e5'}44` },
            { offset: 1, color: `${appSettings.brandColor || '#4f46e5'}00` }
          ]
        }
      },
      lineStyle: { width: 4 }
    }]
  };

  const collectionRate = stats.totalSessions > 0 ? Math.round((stats.totalPaidSessions / stats.totalSessions) * 100) : 0;

  if (!calculateDashboardStats) {
    return (
      <div className="p-10 text-center bg-white rounded-3xl border-2 border-dashed border-gray-100">
        <p className="text-gray-500 font-bold">Initializing reporting engine...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Tab Navigation */}
      <div className="bg-white p-2 rounded-[2rem] shadow-sm border border-gray-100 inline-flex gap-2">
        <button 
          onClick={() => setActiveTab('overview')}
          className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'overview' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
        >
          <SafeIcon icon={FiActivity} /> Financial Overview
        </button>
        <button 
          onClick={() => setActiveTab('tutor-sessions')}
          className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'tutor-sessions' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
        >
          <SafeIcon icon={FiList} /> Tutor Performance
        </button>
      </div>

      {activeTab === 'overview' ? (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'Total Revenue', value: formatCurrency(stats.totalRevenue), icon: FiDollarSign, color: 'indigo' },
              { label: 'Total Sessions', value: stats.totalSessions, icon: FiCalendar, color: 'blue' },
              { label: 'Collection Rate', value: `${collectionRate}%`, icon: FiCheckCircle, color: 'emerald' },
              { label: 'Active Students', value: stats.activeStudents, icon: FiUsers, color: 'orange' }
            ].map((kpi, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100"
              >
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 bg-${kpi.color}-50 text-${kpi.color}-600`}>
                  <SafeIcon icon={kpi.icon} className="text-xl" />
                </div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{kpi.label}</p>
                <h3 className="text-3xl font-black text-gray-900 tracking-tight">{kpi.value}</h3>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white p-10 rounded-[3rem] shadow-sm border border-gray-100">
              <div className="flex justify-between items-center mb-10">
                <div>
                  <h3 className="text-2xl font-black text-gray-900 tracking-tight">Revenue Trend</h3>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Last 6 Months Income</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl font-black text-[10px] uppercase tracking-widest">
                  <SafeIcon icon={FiTrendingUp} /> Real-time
                </div>
              </div>
              <div className="h-[350px]">
                <ReactECharts option={revenueChartOption} style={{ height: '100%' }} />
              </div>
            </div>

            <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-gray-100">
              <h3 className="text-2xl font-black text-gray-900 tracking-tight mb-8">Performance</h3>
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between text-xs font-black uppercase tracking-widest text-gray-400 mb-3">
                    <span>Payment Completion</span>
                    <span className="text-indigo-600">{collectionRate}%</span>
                  </div>
                  <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }} 
                      animate={{ width: `${collectionRate}%` }} 
                      className="h-full bg-indigo-600" 
                      style={{ backgroundColor: appSettings.brandColor }} 
                    />
                  </div>
                </div>

                <div className="pt-8 border-t border-gray-50">
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Subject Distribution</h4>
                  <div className="space-y-4">
                    {['Math', 'Science', 'English'].map((sub, i) => {
                      const count = students.filter(s => s.subjects?.some(subj => subj.subject === sub)).length;
                      const percentage = Math.round((count / (students.length || 1)) * 100);
                      return (
                        <div key={i} className="flex items-center gap-4">
                          <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400">
                            <SafeIcon icon={FiBarChart2} className="text-xs" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between text-xs font-bold text-gray-700 mb-1">
                              <span>{sub}</span>
                              <span>{count} Students</span>
                            </div>
                            <div className="h-1.5 bg-gray-50 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-200" style={{ width: `${percentage}%` }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <TutorSessionsReport />
      )}
    </div>
  );
};

export default ReportManagement;